import classNames from 'classnames'
import { FC, useCallback, useMemo } from 'react'

import { CONFIG_ENABLE_HOLIDAY_CUTOFF } from '@src/config/env'
import { EXPERIMENTS } from '@src/experiments'
import { isValentineSlug } from '@src/pages/[...slug]/components/collection/collection.helpers'
import Product from '@src/quince-site-common/src/domains/Product'
import { PRODUCT_TYPES } from '@src/quince-site-common/src/domains/Product/BundleProduct'
import DeliveryDetailsByColorType, {
  DeliveryDetailsType,
  DeliveryOptions,
} from '@src/quince-site-common/src/domains/ProductDeliveryDetails'
import { ProductOptionDisplayType, ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import ProductTag from '@src/quince-site-common/src/domains/ProductTag'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import useExperimentBucket from '@src/quince-site-common/src/hooks/useExperimentBucket'
import { getFormattedAverageRating } from '@src/quince-site-common/src/utils/getFormattedAverageRating'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import {
  getFormattedValentineDate,
  isValentinePhase2orHigher,
  isValentinePhase4,
} from '@src/quince-site-common/src/utils/valentineUtil'
import OptionSelector from '@src/quince-site-pdp/src/atoms/optionSelector/OptionSelector'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { Icon } from '../../../atoms'
import { TrackConfig } from '../../../hocs/withTracking'
import FilledStar from '../icons/filledStar.inline.svg'
import { useProductCardContext } from '../product-card-context/product-card.context'
import { getFilteredOptionValueList } from '../productCard.helpers'

import styles from './product-card-footer.module.scss'

export const OPTION_DISPLAY_TYPES_VS_CLASS_NAME = {
  [ProductOptionDisplayType.COLOR]: styles.colorOptionProductCard,
  [ProductOptionDisplayType.IMAGE]: styles.colorOptionProductCard,
}

const currentDateTimeInUTC = Date.parse(new Date().toISOString())
const isPhase2orHigher = isValentinePhase2orHigher(currentDateTimeInUTC)
const isPhase4 = isValentinePhase4(currentDateTimeInUTC)

const SHOW_DELIVERY_MESSAGE = isPhase2orHigher
const SHOW_ARRIVAL_FOR_EXPRESS = isPhase4

function getDeliveryMessage(
  deliveryInfo: DeliveryDetailsType,
  deliveryType: DeliveryOptions,
  showCompleteDate?: boolean,
) {
  const deliveryInfoForShippingMethod = deliveryInfo[deliveryType]
  if (!deliveryInfoForShippingMethod.is_delivered_by_date) {
    return undefined
  }
  if (showCompleteDate) {
    const deliveryDate = getFormattedValentineDate(deliveryInfoForShippingMethod.estimated_delivery_date.maximum)
    return deliveryDate ? `Arrives by ${deliveryDate}` : undefined
  } else return `Arrives by V-Day`
}

function BottomTagComponent({
  isPreorder,
  deliveryMessage,
  isWhiteGloveDelivery,
}: {
  isPreorder?: boolean
  deliveryMessage?: string
  isWhiteGloveDelivery?: boolean
}) {
  if (
    isBrowser() &&
    deliveryMessage &&
    CONFIG_ENABLE_HOLIDAY_CUTOFF &&
    isValentineSlug(window.location.pathname.substring(1))
  ) {
    return <div className={classNames(styles.bottomTag, styles.getItBy24)}>{deliveryMessage}</div>
  }

  if (isWhiteGloveDelivery) {
    return <div className={styles.whiteglove}>Free white glove delivery</div>
  }

  if (isPreorder) {
    return <div className={classNames(styles.bottomTag, styles.preorder)}>PREORDER</div>
  }

  return null
}

export interface ProductCardFooterProps {
  hideReviews?: boolean
  hideOptions?: boolean
  isPreorder?: boolean
  shouldShowMore?: boolean
  optionLimit?: number
  onShowMoreClick?: (params: { selectedOptions: ProductVariantOption[] }) => void
  getItBy24?: boolean
  deliveryInfo?: DeliveryDetailsByColorType
  productViewTracker?: string
  applicableTags?: ProductTag[]
  productClickTrackConfig?: TrackConfig
  isWhiteGloveDelivery?: boolean
}

export const ProductCardFooter: FC<ProductCardFooterProps> = ({
  hideReviews,
  hideOptions,
  isPreorder,
  shouldShowMore,
  optionLimit,
  onShowMoreClick,
  getItBy24,
  deliveryInfo,
  productViewTracker,
  applicableTags,
  productClickTrackConfig,
  isWhiteGloveDelivery,
}) => {
  const { product, selectedOptions, setSelectedOptions } = useProductCardContext()
  const experimentBucket = useExperimentBucket(EXPERIMENTS.VDAY_CUTOFF_MESSAGE.experimentName)

  const productOptions = useMemo(
    () =>
      product.type === PRODUCT_TYPES.BUNDLE
        ? product.productOptions
        : productHelper.getCascadingProductOptions(product, []),
    [product],
  )

  const formattedAvgRating = getFormattedAverageRating(product.productReviewSummary?.averageRating)
  const selectedOptionValue = selectedOptions[0]?.value

  const optionValueList = useMemo(
    () =>
      getFilteredOptionValueList(
        productHelper.getProductOptionToDisplayInProductCard(productOptions)?.values,
        selectedOptionValue,
      ),
    [productOptions, selectedOptionValue],
  )

  const onOptionSelect = useCallback(
    (optionValue: string) => {
      setSelectedOptions([
        new ProductVariantOption({
          name: productOptions[0].name,
          value: optionValue,
        }),
      ])
      if (productViewTracker) {
        const color =
          (productOptions[0].name === ProductOptionName.Color
            ? optionValue
            : selectedOptions.find(so => so.name === ProductOptionName.Color)?.value) || null
        const tags = Array.from(
          new Set(
            [...(applicableTags || []), ...product.tags]
              .filter(tag => tag.options.some(opt => opt.value === color) || tag.options.length === 0)
              .map(tag => tag.value),
          ),
        )
        const selectedVariants = productHelper.getVariantsForOptions(product, selectedOptions)
        trackEvent(TELEMETRY_EVENT_NAMES.VARIANT_SELECT, {
          color,
          product_id: product.shopifyProductId,
          internalVariantIds: selectedVariants?.map(sv => sv.id),
          variantIds: selectedVariants?.map(sv => sv.shopifyVariantId),
          product_options: selectedOptions,
          product_options_all: product.productOptions,
          tags,
          stock_info: null,
          entity_id: product.id,
          entity_type: 'product_id',
          productType: (product as Product).productTypeName,
          productTitle: product.title,
          variantTitle: selectedVariants[0]?.title,
          price: selectedVariants[0]?.price,
          is_product_card: true,
          division: product.classification?.division?.name,
          department: product.classification?.department?.name,
          subdepartment: product.classification?.subDepartment?.name,
          product_option_name: 'Color',
          product_option_value: optionValue,
          element_name: productClickTrackConfig?.context?.contentContainerName || null,
          element_display_name: productClickTrackConfig?.context?.contentContainerDisplayName || null,
          element_type: productClickTrackConfig?.context?.contentContainerType || null,
          element_id: productClickTrackConfig?.context?.trackingId || null,
          element_position: productClickTrackConfig?.context?.contentContainerPosition || null,
          context: {
            productProperties: getProductPropertiesForTracking(
              product as Product,
              selectedVariants[0]?.shopifyVariantId?.toString(),
            ),
          },
        })
      }
    },
    [productOptions],
  )

  const isColorOption = optionValueList.some(
    option =>
      option.displayType === ProductOptionDisplayType.COLOR || option.displayType === ProductOptionDisplayType.IMAGE,
  )

  const showMoreOption = isColorOption && shouldShowMore

  const deliveryInfoForSelection = deliveryInfo?.[selectedOptions[0]?.value]
  const deliveryMessage =
    SHOW_DELIVERY_MESSAGE &&
    deliveryInfoForSelection &&
    experimentBucket !== EXPERIMENTS.VDAY_CUTOFF_MESSAGE.bucketNames.control
      ? getDeliveryMessage(
          deliveryInfoForSelection,
          SHOW_ARRIVAL_FOR_EXPRESS ? DeliveryOptions.EXPRESS : DeliveryOptions.STANDARD,
          experimentBucket === EXPERIMENTS.VDAY_CUTOFF_MESSAGE.bucketNames.showDate,
        )
      : undefined

  const handleShowMoreClick = useCallback(() => {
    onShowMoreClick?.({ selectedOptions })
  }, [onShowMoreClick, selectedOptions])

  return (
    <div className={styles.productCardFooter}>
      {!hideReviews && formattedAvgRating && (
        <div className={styles.rating}>
          <Icon className={styles.rating_icon} iconSvg={FilledStar} />
          <span className={styles.rating_text}>{formattedAvgRating}</span>
        </div>
      )}
      {!hideOptions && !productOptions[0]?.isHidden && !getShouldHideOptions(product.id) && (
        <OptionSelector
          optionDisplayTypesVsClassName={OPTION_DISPLAY_TYPES_VS_CLASS_NAME}
          optionValueList={optionValueList}
          className={styles.colorOptionWrapper}
          optionWrapperClassName={styles.colorOptionWrapper__optionWrapper}
          selectedValue={selectedOptions[0]?.value}
          isAllRoundOptions
          onSelect={onOptionSelect}
          showMoreOption={showMoreOption}
          optionLimit={optionLimit}
          onShowMoreClick={handleShowMoreClick}
        />
      )}
      {/* <EarlyShipmentTimeLabel productId={product?.shopifyProductId} /> */}
      <BottomTagComponent
        isPreorder={isPreorder}
        getItBy24={getItBy24}
        deliveryMessage={deliveryMessage}
        isWhiteGloveDelivery={isWhiteGloveDelivery}
      />
    </div>
  )
}

const getShouldHideOptions = (productId: number | string) => {
  const HIDE_OPTIONS_PRODUCT_IDS = new Set(['44'])
  return HIDE_OPTIONS_PRODUCT_IDS.has(String(productId))
}
